// src/components/DashboardAdmin/ICTData/ictCatalog/RelatedIct.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteRelated,
  patchRelatedStatus,
  updateRelated,
  insertRelated,
  fetchRelatedByIctId,
} from "../../../../../store/ictRelatedSlice";

const RelatedIct = ({ ictId, onClose, showInsertForm = false }) => {
  const dispatch = useDispatch();
  const { relatedIcts = [], loading, error } = useSelector(
    (state) => state.ictRelated || {}
  );
  const { ictData = [] } = useSelector((state) => state.ui || {});

  const [editId, setEditId] = useState(null);
  const [editRelatedId, setEditRelatedId] = useState("");

  // 🔹 For insert: user types name, we store selected ICT id separately
  const [relatedSearch, setRelatedSearch] = useState("");       // visible text (ictName)
  const [selectedRelatedId, setSelectedRelatedId] = useState(""); // actual ID to save
  const [relatedSuggestions, setRelatedSuggestions] = useState([]);

  // 🔹 FETCH related ICTs when opening view mode
  useEffect(() => {
    if (ictId && !showInsertForm) {
      dispatch(fetchRelatedByIctId(ictId));
    }
  }, [dispatch, ictId, showInsertForm]);

  // helper: get ICT name from id
  const getIctName = (id) => {
    const item = ictData.find((i) => i.id === id);
    return item ? item.ictName : "";
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this related ICT?")) {
      dispatch(deleteRelated(id));
    }
  };

  const handleStatusToggle = (item) => {
    const newStatus = item.status === "A" ? "D" : "A";
    dispatch(patchRelatedStatus({ id: item.id, status: newStatus }));
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setEditRelatedId(item.relatedIctId);
  };

  const handleSave = (item) => {
    dispatch(
      updateRelated({
        id: item.id,
        ictId: item.ictId, // keep same
        relatedIctId: Number(editRelatedId),
        status: item.status,
      })
    );
    setEditId(null);
    setEditRelatedId("");
  };

  const handleInsert = () => {
    if (!selectedRelatedId) {
      alert("Please select a valid related ICT from the suggestions.");
      return;
    }

    dispatch(
      insertRelated({
        ictId: Number(ictId),
        relatedIctId: Number(selectedRelatedId),
        status: "A",
      })
    );
    setRelatedSearch("");
    setSelectedRelatedId("");
    setRelatedSuggestions([]);
    onClose(); // optional close after insert
  };

  // 🔹 Handle typing in related ICT search (by name)
  const handleRelatedSearchChange = (e) => {
    const value = e.target.value;
    setRelatedSearch(value);

    if (!value.trim()) {
      setRelatedSuggestions([]);
      setSelectedRelatedId("");
    } else {
      const matches = ictData.filter((ict) =>
        ict.ictName.toLowerCase().includes(value.toLowerCase())
      );
      setRelatedSuggestions(matches);
    }
  };

  // 🔹 When clicking a suggestion
  const handleSelectRelatedIct = (ict) => {
    setSelectedRelatedId(ict.id);
    setRelatedSearch(ict.ictName);
    setRelatedSuggestions([]);
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content large">
        <div className="popup-header">
          <h3>
            {showInsertForm
              ? "Add Related ICT"
              : `Related ICTs for ICT ID: ${ictId}`}
            <button className="close-btn" onClick={onClose}>
              <i className="fa fa-times"></i>
            </button>
          </h3>
        </div>

        {loading && !showInsertForm && <p>Loading related ICTs...</p>}
        {error && !showInsertForm && (
          <p className="error">
            {typeof error === "string" ? error : JSON.stringify(error)}
          </p>
        )}

        {showInsertForm ? (
          <div>
            <label>Related ICT (by Name):</label>
            <input
              type="text"
              value={relatedSearch}
              onChange={handleRelatedSearchChange}
              placeholder="Type ICT name..."
              className="input"
            />

            {relatedSuggestions.length > 0 && (
              <div className="suggestions-dropdown">
                {relatedSuggestions.map((ict) => (
                  <div
                    key={ict.id}
                    className="suggestion-item"
                    onClick={() => handleSelectRelatedIct(ict)}
                  >
                    {ict.ictName} (ID: {ict.id})
                  </div>
                ))}
              </div>
            )}

            {selectedRelatedId && (
              <p style={{ fontSize: "0.85em", marginTop: "0.5rem" }}>
                Selected:{" "}
                <b>
                  {getIctName(Number(selectedRelatedId))} (ID:{" "}
                  {selectedRelatedId})
                </b>
              </p>
            )}

            <hr />
            <button onClick={handleInsert}>Insert</button>
          </div>
        ) : (
          <>
            {relatedIcts.length === 0 ? (
              <p>No related ICTs found.</p>
            ) : (
              <table className="table styled-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Related ICT ID</th>
                    <th>Related ICT Name</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {relatedIcts.map((item, index) => (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td>
                        {editId === item.id ? (
                          <input
                            type="number"
                            value={editRelatedId}
                            onChange={(e) =>
                              setEditRelatedId(e.target.value)
                            }
                          />
                        ) : (
                          item.relatedIctId
                        )}
                      </td>
                      <td>{getIctName(item.relatedIctId)}</td>
                      <td>
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={item.status === "A"}
                            onChange={() => handleStatusToggle(item)}
                          />
                          <span className="slider round"></span>
                        </label>
                      </td>
                      <td>
                        {editId === item.id ? (
                          <i
                            className="fa fa-save action-icon saveIcon"
                            onClick={() => handleSave(item)}
                          ></i>
                        ) : (
                          <i
                            className="fa fa-edit action-icon editIcon"
                            onClick={() => handleEdit(item)}
                          ></i>
                        )}
                        <i
                          className="fa fa-trash action-icon deleteIcon"
                          onClick={() => handleDelete(item.id)}
                        ></i>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RelatedIct;
